import * as React from "react"
import { Avatar as BaseAvatar } from "@base-ui/react/avatar"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@lib/cn"

const avatarVariants = cva(
  [
    "relative inline-flex shrink-0 items-center justify-center overflow-hidden rounded-pill",
    "bg-cloverhound-gray-200 text-cloverhound-gray-800 dark:bg-cloverhound-gray-800 dark:text-cloverhound-gray-300",
    "font-semibold select-none",
  ].join(" "),
  {
    variants: {
      size: {
        xs: "size-4 text-[9px]",
        sm: "size-5 text-[10px]",
        md: "size-7 text-mini",
        lg: "size-9 text-small",
        xl: "size-12 text-regular",
      },
    },
    defaultVariants: { size: "md" },
  },
)

export interface AvatarProps
  extends React.ComponentPropsWithoutRef<typeof BaseAvatar.Root>,
    VariantProps<typeof avatarVariants> {
  src?: string
  alt?: string
  fallback?: React.ReactNode
}

export const Avatar = React.forwardRef<HTMLDivElement, AvatarProps>(
  ({ className, size, src, alt, fallback, ...props }, ref) => (
    <BaseAvatar.Root ref={ref} className={cn(avatarVariants({ size }), className)} {...props}>
      {src && <BaseAvatar.Image src={src} alt={alt} className="size-full object-cover" />}
      <BaseAvatar.Fallback className="flex size-full items-center justify-center">
        {fallback ?? (alt ? initials(alt) : "?")}
      </BaseAvatar.Fallback>
    </BaseAvatar.Root>
  ),
)
Avatar.displayName = "Avatar"

function initials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]!.toUpperCase())
    .join("")
}
